import { Link } from "react-router-dom";
import { getChannel } from "@/mocks/channels";
import type { Topic } from "@/mocks/topics";
import { formatNum } from "@/lib/format";

const accentMap: Record<string, string> = {
  primary: "bg-primary-500 text-background-50",
  accent: "bg-accent-500 text-background-50",
  secondary: "bg-secondary-600 text-background-50",
};

export default function TopicCard({ topic, index = -1 }: { topic: Topic; index?: number }) {
  const ch = getChannel(topic.channel);
  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-2xl border border-background-200 bg-background-50 transition-shadow hover:shadow-lg">
      <Link to={`/topics/${topic.slug}`} className="relative block overflow-hidden">
        <img
          src={topic.image}
          alt={`${topic.title} - 网友热议观点汇总`}
          className="h-44 w-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute left-3 top-3 flex items-center gap-2">
          <span
            className={`rounded-full px-2.5 py-1 text-xs font-semibold ${accentMap[ch?.accent ?? "secondary"]}`}
          >
            {ch?.label ?? "话题"}
          </span>
          {index === 0 && (
            <span className="rounded-full bg-background-50/85 px-2.5 py-1 text-xs font-bold text-primary-600 backdrop-blur-sm">
              <i className="ri-fire-fill" aria-hidden="true" /> 高热
            </span>
          )}
        </div>
        <span className="absolute bottom-3 right-3 flex items-center gap-1 rounded-full bg-foreground-950/45 px-2.5 py-1 text-xs text-background-50 backdrop-blur-sm">
          <i className="ri-chat-heart-line" aria-hidden="true" />
          {formatNum(topic.comments)}
        </span>
      </Link>
      <div className="flex flex-1 flex-col p-4">
        <h3 className="line-clamp-2 font-heading text-lg font-semibold leading-snug text-foreground-900">
          <Link to={`/topics/${topic.slug}`} className="transition-colors hover:text-primary-700">
            {topic.title}
          </Link>
        </h3>
        <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">{topic.dek}</p>

        <div className="mt-3 flex flex-wrap gap-1.5">
          {topic.tags.map((tag) => (
            <span key={tag} className="rounded-full bg-background-200/70 px-2 py-0.5 text-xs text-foreground-500">
              #{tag}
            </span>
          ))}
        </div>

        {/* 态度迷你条 */}
        <div className="mt-4">
          <div className="flex h-1.5 w-full overflow-hidden rounded-full">
            <span className="bg-accent-500" style={{ width: `${topic.sentiment.support}%` }} />
            <span className="bg-secondary-300" style={{ width: `${topic.sentiment.neutral}%` }} />
            <span className="bg-primary-500" style={{ width: `${topic.sentiment.oppose}%` }} />
          </div>
          <p className="mt-1.5 text-xs text-foreground-400">
            <span className="text-accent-700">支持 {topic.sentiment.support}%</span>
            <span className="mx-1.5 text-foreground-300">·</span>
            中性 {topic.sentiment.neutral}%
            <span className="mx-1.5 text-foreground-300">·</span>
            <span className="text-primary-700">反对 {topic.sentiment.oppose}%</span>
          </p>
        </div>

        <div className="mt-auto flex items-center justify-between pt-3 text-xs text-foreground-400">
          <span className="flex items-center gap-1">
            <i className="ri-flashlight-fill text-accent-500" aria-hidden="true" />
            热度 {formatNum(topic.heat)}
          </span>
          <span>{topic.updated}</span>
        </div>
      </div>
    </article>
  );
}